'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCreateDemoStore, useEraseStore } from '@/lib/api/demo';

export default function DemoPage() {
    const { t } = useTranslation('settings');
    const [storeId, setStoreId] = React.useState('');
    const [showEraseDialog, setShowEraseDialog] = React.useState(false);

    const createDemoMutation = useCreateDemoStore();
    const eraseMutation = useEraseStore();

    const handleCreateDemo = () => {
        createDemoMutation.mutate({
            name: 'Demo Store',
        });
    };

    const handleEraseStore = () => {
        if (!storeId.trim()) {
            return;
        }
        setShowEraseDialog(true);
    };

    const confirmEraseStore = () => {
        eraseMutation.mutate(
            { storeId: storeId.trim() },
            {
                onSuccess: () => {
                    setStoreId('');
                    setShowEraseDialog(false);
                },
            }
        );
    };

    return (
        <div className="flex flex-col space-y-4 sm:space-y-6">
            <div>
                <h2 className="text-xl font-semibold">{t('demo.heading')}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                    {t('demo.headingDescription')}
                </p>
            </div>

            {/* Create Demo Store */}
            <Card>
                <CardHeader>
                    <CardTitle>{t('demo.createStore.title')}</CardTitle>
                    <CardDescription>
                        {t('demo.createStore.description')}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Button
                        onClick={handleCreateDemo}
                        disabled={createDemoMutation.isPending}
                    >
                        {createDemoMutation.isPending ? t('demo.createStore.creating') : t('demo.createStore.button')}
                    </Button>
                </CardContent>
            </Card>

            {/* Store Eraser */}
            <Card className="border-destructive">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                        {t('demo.eraser.title')}
                    </CardTitle>
                    <CardDescription>
                        {t('demo.eraser.description')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="storeId">{t('demo.eraser.storeIdLabel')}</Label>
                        <Input
                            id="storeId"
                            type="text"
                            placeholder={t('demo.eraser.storeIdPlaceholder')}
                            value={storeId}
                            onChange={(e) => setStoreId(e.target.value)}
                        />
                    </div>
                    <Button
                        variant="destructive"
                        onClick={handleEraseStore}
                        disabled={!storeId.trim() || eraseMutation.isPending}
                    >
                        {eraseMutation.isPending ? t('demo.eraser.erasing') : t('demo.eraser.button')}
                    </Button>
                </CardContent>
            </Card>

            {/* Confirmation Dialog */}
            <AlertDialog open={showEraseDialog} onOpenChange={setShowEraseDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('demo.confirmDialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                            <p className="font-semibold">
                                {t('demo.confirmDialog.warning', { storeId })}
                            </p>
                            <ul className="list-disc list-inside space-y-1 text-sm">
                                <li>{t('demo.confirmDialog.items.customers')}</li>
                                <li>{t('demo.confirmDialog.items.orders')}</li>
                                <li>{t('demo.confirmDialog.items.payments')}</li>
                                <li>{t('demo.confirmDialog.items.subscriptions')}</li>
                                <li>{t('demo.confirmDialog.items.funnels')}</li>
                                <li>{t('demo.confirmDialog.items.cmsRoutes')}</li>
                                <li>{t('demo.confirmDialog.items.emailTemplates')}</li>
                                <li>{t('demo.confirmDialog.items.integrations')}</li>
                                <li>{t('demo.confirmDialog.items.products')}</li>
                                <li>{t('demo.confirmDialog.items.clubs')}</li>
                                <li>{t('demo.confirmDialog.items.settings')}</li>
                            </ul>
                            <p className="font-semibold mt-4">
                                {t('demo.confirmDialog.confirmation')}
                            </p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('demo.confirmDialog.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmEraseStore}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {t('demo.confirmDialog.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
